import { useId, type ReactNode, type Ref } from 'react'
import { CATEGORY_PALETTE } from '@core/utils/categoryVisual'

/**
 * Предметные иконки категорий.
 *
 * Раньше категория получала абстрактный глиф (круг, звезда, волна) — кассир
 * не мог узнать отдел по картинке. Теперь иконка выбирается по смыслу
 * названия: «Молочные товары» — бутылки молока, «Сосиски» — сосиски и т.д.
 *
 * Все иконки белые и рисуются поверх градиента категории (viewBox 96×96),
 * поэтому не зависят от темы и не требуют отдельных файлов.
 */

export type CategoryIconKind =
  | 'milk'
  | 'milkWeight'
  | 'drink'
  | 'can'
  | 'sauce'
  | 'icecream'
  | 'pasta'
  | 'spice'
  | 'dumplings'
  | 'cheese'
  | 'sausage'
  | 'sausageSmall'
  | 'sausageBoiled'
  | 'sausageSmoked'
  | 'sausageDry'
  | 'pate'
  | 'cup'
  | 'candy'
  | 'fish'
  | 'snowflake'
  | 'bread'
  | 'apple'
  | 'basket'

const WHITE = '#fff'

/**
 * Порядок правил важен: узкие случаи («молочная продукция вес», «сырокопчёные»)
 * идут раньше широких («молочные», «колбасы»).
 */
const RULES: Array<[RegExp, CategoryIconKind]> = [
  [/молочн[а-я]*\s*(продукц|товар)[а-я]*\s*вес|весов/, 'milkWeight'],
  [/сырокопч|сыровял|нарезк/, 'sausageDry'],
  [/копчен|копчён/, 'sausageSmoked'],
  [/вар[её]н[а-я]*\s*колбас/, 'sausageBoiled'],
  [/сардел/, 'sausageSmall'],
  [/сосиск/, 'sausage'],
  [/колбас/, 'sausageSmoked'],
  [/молочн|молоко|кефир|творог|йогурт|ряженк|сметан/, 'milk'],
  [/напит|сок|вода|лимонад|квас|энергетик/, 'drink'],
  [/консерв|туш[её]нк|горошек|кукуруз|оливк|маслин/, 'can'],
  [/соус|майонез|кетчуп|горчиц|аджик/, 'sauce'],
  [/морожен|эскимо|пломбир/, 'icecream'],
  [/макарон|круп|масло|рис|греч|пшено|овсян|мука/, 'pasta'],
  [/специ|приправ|перец|соль|корица/, 'spice'],
  [/полуфабрикат|пельмен|вареник|котлет|блин|голубц/, 'dumplings'],
  [/деликатес|сыр/, 'cheese'],
  [/паштет|ветчин|жарен|буженин/, 'pate'],
  [/чай|кофе|какао/, 'cup'],
  [/сладост|конфет|шоколад|печень|торт|вафл|зефир|мармелад/, 'candy'],
  [/морепродукт|рыб|кревет|мидии|кальмар|икр/, 'fish'],
  [/заморо|замор|лёд|лед/, 'snowflake'],
  [/хлеб|батон|булк|лаваш|багет/, 'bread'],
  [/овощ|фрукт|яблок|ягод|банан|цитрус/, 'apple']
]

/** Определить иконку по названию категории. */
export function pickCategoryIcon(name: string): CategoryIconKind {
  const normalized = name.trim().toLowerCase().replace(/ё/g, 'е')
  for (const [pattern, kind] of RULES) {
    if (pattern.test(normalized)) return kind
  }
  return 'basket'
}

function Milk() {
  return (
    <>
      {/* Две бутылки молока */}
      <path d="M28 38h12v10l4 6v26a4 4 0 0 1-4 4H28a4 4 0 0 1-4-4V54l4-6V38Z" fill={WHITE} />
      <rect x="30" y="30" width="8" height="9" rx="2" fill={WHITE} opacity="0.75" />
      <path d="M56 34h12v12l4 6v28a4 4 0 0 1-4 4H56a4 4 0 0 1-4-4V52l4-6V34Z" fill={WHITE} opacity="0.85" />
      <rect x="58" y="26" width="8" height="9" rx="2" fill={WHITE} opacity="0.6" />
    </>
  )
}

function MilkWeight() {
  return (
    <>
      {/* Кувшин и капля */}
      <path d="M26 42h34l-4 32a6 6 0 0 1-6 5H36a6 6 0 0 1-6-5l-4-32Z" fill={WHITE} />
      <path d="M60 48h8a8 8 0 0 1 0 16h-6" fill="none" stroke={WHITE} strokeWidth="6" strokeLinecap="round" />
      <path d="M43 18c6 8 10 13 10 18a10 10 0 1 1-20 0c0-5 4-10 10-18Z" fill={WHITE} opacity="0.7" />
    </>
  )
}

function Drink() {
  return (
    <>
      {/* Стакан с трубочкой */}
      <path d="M30 32h36l-5 42a5 5 0 0 1-5 4H40a5 5 0 0 1-5-4l-5-42Z" fill={WHITE} />
      <path d="M34 46h28l-3 22H37l-3-22Z" fill="#0b1220" opacity="0.18" />
      <path d="M62 22l12-10" stroke={WHITE} strokeWidth="6" strokeLinecap="round" opacity="0.8" />
    </>
  )
}

function Can() {
  return (
    <>
      {/* Консервная банка */}
      <rect x="26" y="30" width="44" height="42" rx="8" fill={WHITE} />
      <rect x="26" y="42" width="44" height="10" fill="#0b1220" opacity="0.18" />
      <ellipse cx="48" cy="30" rx="22" ry="7" fill={WHITE} opacity="0.7" />
    </>
  )
}

function Sauce() {
  return (
    <>
      {/* Бутылка соуса */}
      <path d="M38 24h20v8l6 10v34a6 6 0 0 1-6 6H38a6 6 0 0 1-6-6V42l6-10v-8Z" fill={WHITE} />
      <path d="M44 24V16h8v8" fill={WHITE} opacity="0.7" />
      <path d="M36 52h24v14H36Z" fill="#0b1220" opacity="0.18" />
    </>
  )
}

function IceCream() {
  return (
    <>
      {/* Рожок */}
      <path d="M36 44h28L52 82a4 4 0 0 1-8 0L32 44h4Z" fill={WHITE} opacity="0.9" />
      <circle cx="40" cy="36" r="12" fill={WHITE} />
      <circle cx="56" cy="36" r="12" fill={WHITE} opacity="0.85" />
      <circle cx="48" cy="26" r="10" fill={WHITE} opacity="0.7" />
    </>
  )
}

function Pasta() {
  return (
    <>
      {/* Пачка крупы/макарон */}
      <path d="M28 30h40v48a4 4 0 0 1-4 4H32a4 4 0 0 1-4-4V30Z" fill={WHITE} />
      <path d="M28 30l8-10h24l8 10H28Z" fill={WHITE} opacity="0.65" />
      <path d="M40 46v24M48 42v32M56 46v24" stroke="#0b1220" strokeWidth="4" strokeLinecap="round" opacity="0.18" />
    </>
  )
}

function Spice() {
  return (
    <>
      {/* Баночка специй */}
      <rect x="30" y="32" width="36" height="44" rx="6" fill={WHITE} />
      <rect x="34" y="22" width="28" height="12" rx="4" fill={WHITE} opacity="0.7" />
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <circle key={`${row}-${col}`} cx={40 + col * 8} cy={46 + row * 9} r="2.4" fill="#0b1220" opacity="0.22" />
        ))
      )}
    </>
  )
}

function Dumplings() {
  return (
    <>
      {/* Пельмень */}
      <path d="M22 62c0-16 12-28 26-28s26 12 26 28H22Z" fill={WHITE} />
      <path d="M30 60c2-6 6-9 10-9M48 60c2-6 6-9 10-9" stroke="#0b1220" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.18" />
      <ellipse cx="48" cy="64" rx="30" ry="6" fill={WHITE} opacity="0.6" />
    </>
  )
}

function Cheese() {
  return (
    <>
      {/* Кусок сыра с дырками */}
      <path d="M22 64V38l18-12 34 10v28H22Z" fill={WHITE} />
      <circle cx="40" cy="52" r="5" fill="#0b1220" opacity="0.18" />
      <circle cx="58" cy="60" r="4" fill="#0b1220" opacity="0.18" />
      <circle cx="56" cy="44" r="3" fill="#0b1220" opacity="0.18" />
    </>
  )
}

function Sausage() {
  return (
    <>
      {/* Три сосиски */}
      <path d="M20 62c0-8 6-14 14-14h28c8 0 14 6 14 14s-6 14-14 14H34c-8 0-14-6-14-14Z" fill={WHITE} />
      <path d="M26 44c0-7 5-12 12-12h26c7 0 12 5 12 12s-5 12-12 12H38c-7 0-12-5-12-12Z" fill={WHITE} opacity="0.82" />
      <path d="M34 30c0-6 4-10 10-10h22c6 0 10 4 10 10s-4 10-10 10H44c-6 0-10-4-10-10Z" fill={WHITE} opacity="0.64" />
    </>
  )
}

function SausageSmall() {
  return (
    <>
      {/* Мелкие сардельки */}
      <ellipse cx="36" cy="48" rx="16" ry="10" fill={WHITE} />
      <ellipse cx="60" cy="58" rx="16" ry="10" fill={WHITE} opacity="0.8" />
      <ellipse cx="48" cy="36" rx="14" ry="9" fill={WHITE} opacity="0.64" />
    </>
  )
}

function SausageBoiled() {
  return (
    <>
      {/* Варёная колбаса: батон с перевязкой */}
      <path d="M24 58c6-12 14-20 24-20s18 8 24 20c-6 12-14 20-24 20s-18-8-24-20Z" fill={WHITE} />
      <path d="M40 40v36M56 40v36" stroke="#0b1220" strokeWidth="4" opacity="0.18" />
      <circle cx="32" cy="58" r="4" fill="#0b1220" opacity="0.18" />
    </>
  )
}

function SausageSmoked() {
  return (
    <>
      {/* Копчёная колбаса с подвесом */}
      <path d="M30 34c10-6 26-6 36 0v40c-10 6-26 6-36 0V34Z" fill={WHITE} />
      <path d="M30 50h36" stroke="#0b1220" strokeWidth="4" opacity="0.18" />
      <path d="M48 24v10" stroke={WHITE} strokeWidth="5" strokeLinecap="round" opacity="0.7" />
    </>
  )
}

function SausageDry() {
  return (
    <>
      {/* Нарезка сырокопчёной колбасы */}
      <circle cx="36" cy="44" r="13" fill={WHITE} />
      <circle cx="60" cy="44" r="13" fill={WHITE} opacity="0.8" />
      <circle cx="48" cy="64" r="13" fill={WHITE} opacity="0.65" />
      <circle cx="36" cy="44" r="4" fill="#0b1220" opacity="0.16" />
      <circle cx="60" cy="44" r="4" fill="#0b1220" opacity="0.16" />
      <circle cx="48" cy="64" r="4" fill="#0b1220" opacity="0.16" />
    </>
  )
}

function Pate() {
  return (
    <>
      {/* Банка паштета */}
      <rect x="28" y="36" width="40" height="38" rx="7" fill={WHITE} />
      <ellipse cx="48" cy="36" rx="20" ry="6" fill={WHITE} opacity="0.7" />
      <path d="M36 56h24" stroke="#0b1220" strokeWidth="4" strokeLinecap="round" opacity="0.18" />
    </>
  )
}

function Cup() {
  return (
    <>
      {/* Чашка с паром */}
      <path d="M22 46h40v18a14 14 0 0 1-14 14H36a14 14 0 0 1-14-14V46Z" fill={WHITE} />
      <path d="M62 50h6a8 8 0 0 1 0 16h-6" fill="none" stroke={WHITE} strokeWidth="6" />
      <path d="M36 22c-4 5-2 8 0 12M50 20c-4 5-2 8 0 12" stroke={WHITE} strokeWidth="4" strokeLinecap="round" opacity="0.65" fill="none" />
    </>
  )
}

function Candy() {
  return (
    <>
      {/* Конфета */}
      <circle cx="48" cy="50" r="16" fill={WHITE} />
      <path d="M32 42L18 34v32l14-8M64 42l14-8v32l-14-8" fill={WHITE} opacity="0.7" />
    </>
  )
}

function Fish() {
  return (
    <>
      {/* Рыба */}
      <path d="M20 52c10-14 26-20 44-16 8 2 12 8 12 16s-4 14-12 16c-18 4-34-2-44-16Z" fill={WHITE} />
      <path d="M20 52L8 38v28l12-14Z" fill={WHITE} opacity="0.7" />
      <circle cx="66" cy="46" r="3" fill="#0b1220" opacity="0.25" />
    </>
  )
}

function Snowflake() {
  return (
    <>
      {/* Снежинка */}
      <g stroke={WHITE} strokeWidth="6" strokeLinecap="round" fill="none">
        <path d="M48 16v64M20 32l56 32M76 32L20 64" />
        <path d="M40 22l8 8 8-8M40 74l8-8 8 8" />
      </g>
    </>
  )
}

function Bread() {
  return (
    <>
      {/* Батон */}
      <path d="M22 60c0-16 12-28 28-28h4c16 0 20 10 20 20s-6 20-20 20H36c-8 0-14-5-14-12Z" fill={WHITE} />
      <path d="M38 44c4-4 9-6 14-6M50 58c4-4 9-6 14-6" stroke="#0b1220" strokeWidth="4" strokeLinecap="round" opacity="0.16" fill="none" />
    </>
  )
}

function Apple() {
  return (
    <>
      {/* Яблоко с листом */}
      <path d="M48 34c8-8 22-4 24 10 2 16-12 34-24 34S22 60 24 44c2-14 16-18 24-10Z" fill={WHITE} />
      <path d="M48 34c0-8 4-14 10-16 0 8-4 14-10 16Z" fill={WHITE} opacity="0.7" />
    </>
  )
}

function Basket() {
  return (
    <>
      {/* Корзина */}
      <path d="M22 42h52l-6 32a6 6 0 0 1-6 5H34a6 6 0 0 1-6-5l-6-32Z" fill={WHITE} />
      <path d="M36 42l8-18M60 42l-8-18" stroke={WHITE} strokeWidth="6" strokeLinecap="round" opacity="0.7" fill="none" />
      <path d="M34 56h28M38 66h20" stroke="#0b1220" strokeWidth="4" strokeLinecap="round" opacity="0.16" />
    </>
  )
}

const ICONS: Record<CategoryIconKind, () => ReactNode> = {
  milk: Milk,
  milkWeight: MilkWeight,
  drink: Drink,
  can: Can,
  sauce: Sauce,
  icecream: IceCream,
  pasta: Pasta,
  spice: Spice,
  dumplings: Dumplings,
  cheese: Cheese,
  sausage: Sausage,
  sausageSmall: SausageSmall,
  sausageBoiled: SausageBoiled,
  sausageSmoked: SausageSmoked,
  sausageDry: SausageDry,
  pate: Pate,
  cup: Cup,
  candy: Candy,
  fish: Fish,
  snowflake: Snowflake,
  bread: Bread,
  apple: Apple,
  basket: Basket
}

/** Содержимое иконки категории (без фона). */
export function CategoryIcon({ name }: { name: string }) {
  const Icon = ICONS[pickCategoryIcon(name)]
  return <Icon />
}

/** Все виды иконок — для галереи выбора в админке. */
export const CATEGORY_ICON_KINDS: CategoryIconKind[] = [
  'milk',
  'milkWeight',
  'drink',
  'can',
  'sauce',
  'icecream',
  'pasta',
  'spice',
  'dumplings',
  'cheese',
  'sausage',
  'sausageSmall',
  'sausageBoiled',
  'sausageSmoked',
  'sausageDry',
  'pate',
  'cup',
  'candy',
  'fish',
  'snowflake',
  'bread',
  'apple',
  'basket'
]

export interface CategoryIconTileProps {
  /** Конкретная иконка (не выводится из названия). */
  kind: CategoryIconKind
  /** Индекс градиента в утверждённой палитре. */
  colorKey: number
  size?: number
  rounded?: number
  /** Ссылка на SVG: нужна, чтобы отрисовать иконку в PNG. */
  svgRef?: Ref<SVGSVGElement>
  className?: string
}

/**
 * Иконка на градиенте: предпросмотр в галерее и источник для рендера в PNG.
 * В отличие от `CategoryGlyph` вид иконки задаётся явно, а не по названию.
 */
export function CategoryIconTile({
  kind,
  colorKey,
  size = 96,
  rounded = 0.25,
  svgRef,
  className
}: CategoryIconTileProps) {
  const rawId = useId()
  const gradientId = `ci-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`
  const gradient =
    CATEGORY_PALETTE[
      ((colorKey % CATEGORY_PALETTE.length) + CATEGORY_PALETTE.length) %
        CATEGORY_PALETTE.length
    ]
  const Icon = ICONS[kind]

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 96 96"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={gradient.from} />
          <stop offset="100%" stopColor={gradient.to} />
        </linearGradient>
      </defs>
      <rect width="96" height="96" rx={96 * rounded} fill={`url(#${gradientId})`} />
      <g opacity="0.94">
        <Icon />
      </g>
    </svg>
  )
}
